import React, { useState, useEffect, useCallback } from 'react';
import {
    Edit,
    Trash2,
    X,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    Eye,
    CheckCircle,
    RefreshCw,
    Wifi,
    WifiOff,
    UserPlus,
    CheckSquare,
    Square,
    MinusSquare,
} from 'lucide-react';
import websiteApi from '../../services/websiteApi';
import './InukaUsersManager.css';

interface LinkedFingerprint {
    _id: string;
    status: string;
    expires_at?: string;
    license_key?: string;
    limits?: {
        max_cameras?: number;
        entry_cameras?: number;
        exit_cameras?: number;
        max_members?: number;
    };
    org_name?: string;
    created_at?: string;
}

interface InukaUser {
    _id: string;
    username: string;
    email: string;
    fullName?: string;
    phone?: string;
    org_name?: string;
    emailVerified: boolean;
    subscriptionStatus: string;
    createdAt: string;
    updatedAt: string;
    linked_fingerprint?: LinkedFingerprint | null;
}

const InukaUsersManager: React.FC = () => {
    const [users, setUsers] = useState<InukaUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    // Modals
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [bulkDeleteFingerprints, setBulkDeleteFingerprints] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState<InukaUser | null>(null);

    const [editingUser, setEditingUser] = useState<InukaUser | null>(null);
    const [syncing, setSyncing] = useState<string | null>(null);
    const [bulkSyncing, setBulkSyncing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Multi-select
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

    // Pagination & Search
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Forms
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        fullName: '',
        email: '',
        phone: '',
        org_name: '',
        subscriptionStatus: 'free',
        emailVerified: false
    });

    // Auto-refresh
    const [autoRefresh, setAutoRefresh] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchUsers = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const response = await websiteApi.getInukaUsers({
                page,
                limit,
                search: debouncedSearch || undefined
            });
            setUsers(response.users);
            setTotalPages(response.totalPages);
            setTotalCount(response.totalCount);
            setError('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch users');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [page, debouncedSearch]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        if (!autoRefresh) return;
        const interval = setInterval(() => {
            fetchUsers(false);
        }, 30000);
        return () => clearInterval(interval);
    }, [autoRefresh, fetchUsers]);

    // Handlers
    const handleAddUser = () => {
        setFormData({
            username: '',
            password: '',
            fullName: '',
            email: '',
            phone: '',
            org_name: '',
            subscriptionStatus: 'free',
            emailVerified: false
        });
        setShowAddModal(true);
    };

    const handleEdit = (user: InukaUser) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            password: '', // Password not shown for edit
            fullName: user.fullName || '',
            email: user.email,
            phone: user.phone || '',
            org_name: user.org_name || '',
            subscriptionStatus: user.subscriptionStatus || 'free',
            emailVerified: user.emailVerified || false
        });
        setShowEditModal(true);
    };

    const handleCreateFinish = async () => {
        try {
            setIsProcessing(true);
            await websiteApi.createInukaUser(formData);
            setSuccessMessage('User created successfully');
            setShowAddModal(false);
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create user');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleEditFinish = async () => {
        if (!editingUser) return;
        try {
            setIsProcessing(true);
            // Don't send password if empty in edit
            const updateData = { ...formData };
            if (!updateData.password) delete (updateData as any).password;

            await websiteApi.updateInukaUser(editingUser._id, updateData);
            setSuccessMessage('User updated successfully');
            setShowEditModal(false);
            setEditingUser(null);
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update user');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (userId: string) => {
        try {
            await websiteApi.deleteInukaUser(userId, false);
            setSuccessMessage('User deleted successfully');
            setShowDeleteConfirm(null);
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete user');
        }
    };

    const handleBulkDelete = async (deleteFingerprints: boolean) => {
        try {
            setIsProcessing(true);
            const userIds = Array.from(selectedUserIds);
            const response = await websiteApi.bulkDeleteInukaUsers(userIds, deleteFingerprints);
            setSuccessMessage(`Successfully deleted ${response.deleted_count} users`);
            setSelectedUserIds(new Set());
            setShowBulkDeleteConfirm(false);
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete users');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSyncUser = async (userId: string) => {
        try {
            setSyncing(userId);
            const response = await websiteApi.syncUserToFingerprint(userId);
            setSuccessMessage(`License ${response.action === 'created' ? 'Created' : 'Updated'}`);
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err: any) {
            setError(err instanceof Error ? err.message : 'Failed to sync user');
        } finally {
            setSyncing(null);
        }
    };

    const handleBulkSync = async () => {
        try {
            setBulkSyncing(true);
            const response = await websiteApi.syncAllUsersToFingerprints();
            setSuccessMessage(
                `Bulk sync complete: ${response.created_count} created, ${response.updated_count} updated`
            );
            await fetchUsers();
            setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to perform bulk sync');
        } finally {
            setBulkSyncing(false);
        }
    };

    // Selection helpers
    const toggleSelectUser = (id: string) => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedUserIds.size === users.length && users.length > 0) {
            setSelectedUserIds(new Set());
        } else {
            setSelectedUserIds(new Set(users.map(u => u._id)));
        }
    };

    const getStatusClass = (status: string) => {
        const key = (status || 'free').toLowerCase();
        if (['active'].includes(key)) return 'wu-badge wu-badge--active';
        if (['trial', 'pending'].includes(key)) return 'wu-badge wu-badge--trial';
        if (['cancelled', 'revoked', 'expired'].includes(key)) return 'wu-badge wu-badge--expired';
        return 'wu-badge wu-badge--free';
    };

    const displayOrg = (user: InukaUser) =>
        user.org_name?.trim() || user.linked_fingerprint?.org_name?.trim() || '—';

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading && users.length === 0) {
        return (
            <div className="wu-page wu-loading">
                <RefreshCw size={20} className="wu-spin" />
                Loading users…
            </div>
        );
    }

    return (
        <div className="wu-page">
            <div className="wu-toolbar">
                <input
                    type="text"
                    className="wu-search"
                    placeholder="Search name, email, org…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="wu-toolbar-note">{totalCount} website users</span>
                <div className="wu-toolbar-actions">
                    {selectedUserIds.size > 0 && (
                        <div className="wu-bulk">
                            <span>{selectedUserIds.size} selected</span>
                            <button type="button" className="wu-btn" onClick={() => setSelectedUserIds(new Set())}>Clear</button>
                            <button type="button" className="wu-btn wu-btn--danger" onClick={() => setShowBulkDeleteConfirm(true)}>
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className={`wu-btn ${autoRefresh ? 'wu-btn--live' : ''}`}
                        onClick={() => setAutoRefresh(!autoRefresh)}
                    >
                        {autoRefresh ? <Wifi size={14} /> : <WifiOff size={14} />}
                        {autoRefresh ? 'Live' : 'Paused'}
                    </button>
                    <button type="button" className="wu-btn" onClick={handleBulkSync} disabled={bulkSyncing}>
                        <RefreshCw size={14} className={bulkSyncing ? 'wu-spin' : ''} />
                        Sync all
                    </button>
                    <button type="button" className="wu-btn" onClick={() => fetchUsers()}>
                        <RefreshCw size={14} className={loading ? 'wu-spin' : ''} />
                    </button>
                    <button type="button" className="wu-btn wu-btn--primary" onClick={handleAddUser}>
                        <UserPlus size={14} /> New user
                    </button>
                </div>
            </div>

            {error && (
                <div className="wu-toast wu-toast--err">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError('')} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}
            {successMessage && (
                <div className="wu-toast wu-toast--ok">
                    <span>{successMessage}</span>
                    <button type="button" onClick={() => setSuccessMessage('')} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            <div className="wu-table-panel">
                {users.length === 0 && !loading ? (
                    <div className="wu-empty">
                        <p>{searchTerm ? 'No users match your search' : 'No website users yet'}</p>
                        <button type="button" className="wu-btn wu-btn--primary" onClick={handleAddUser}>
                            <UserPlus size={14} /> Add user
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="wu-table-scroll">
                            <table className="wu-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <button type="button" className="wu-check" onClick={toggleSelectAll}>
                                                {selectedUserIds.size === users.length && users.length > 0 ? (
                                                    <CheckSquare size={16} />
                                                ) : selectedUserIds.size > 0 ? (
                                                    <MinusSquare size={16} />
                                                ) : (
                                                    <Square size={16} />
                                                )}
                                            </button>
                                        </th>
                                        <th>Organization</th>
                                        <th>Plan</th>
                                        <th>License</th>
                                        <th>Joined</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => {
                                        const isSelected = selectedUserIds.has(user._id);
                                        return (
                                            <tr key={user._id} className={isSelected ? 'wu-row--selected' : ''}>
                                                <td>
                                                    <button type="button" className="wu-check" onClick={() => toggleSelectUser(user._id)}>
                                                        {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                                                    </button>
                                                </td>
                                                <td title={`${displayOrg(user)} · ${user.email}`}>
                                                    <span className="wu-org-name">{displayOrg(user)}</span>
                                                    <span className="wu-user-line">
                                                        {user.fullName || user.username} · {user.email}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={getStatusClass(user.subscriptionStatus)}>
                                                        {user.subscriptionStatus || 'free'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {user.linked_fingerprint ? (
                                                        <span className={`wu-fp wu-fp--linked`}>
                                                            {user.linked_fingerprint.status}
                                                        </span>
                                                    ) : (
                                                        <span className="wu-fp">Unlinked</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className="wu-date">{formatDate(user.createdAt)}</span>
                                                </td>
                                                <td>
                                                    <div className="wu-actions">
                                                        <button
                                                            type="button"
                                                            className="wu-icon-btn"
                                                            title="Sync license"
                                                            onClick={() => handleSyncUser(user._id)}
                                                            disabled={syncing === user._id}
                                                        >
                                                            <RefreshCw size={14} className={syncing === user._id ? 'wu-spin' : ''} />
                                                        </button>
                                                        <button type="button" className="wu-icon-btn" title="View" onClick={() => setShowDetailsModal(user)}>
                                                            <Eye size={14} />
                                                        </button>
                                                        <button type="button" className="wu-icon-btn" title="Edit" onClick={() => handleEdit(user)}>
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="wu-icon-btn wu-icon-btn--danger"
                                                            title="Delete"
                                                            onClick={() => setShowDeleteConfirm(user._id)}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="wu-pagination">
                                <span>Page {page} of {totalPages}</span>
                                <div className="wu-pagination-btns">
                                    <button type="button" className="wu-btn" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                        <ChevronLeft size={14} /> Prev
                                    </button>
                                    <button type="button" className="wu-btn" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                                        Next <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Create / Edit modal */}
            {(showAddModal || showEditModal) && (
                <div className="wu-overlay" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
                    <div className="wu-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="wu-modal-header">
                            <h3 className="wu-modal-title">{showAddModal ? 'New user' : 'Edit user'}</h3>
                            <button type="button" className="wu-modal-close" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} aria-label="Close">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="wu-modal-body">
                            <div className="wu-form-row">
                                <div className="wu-field">
                                    <label htmlFor="wu-username">Username</label>
                                    <input id="wu-username" type="text" value={formData.username} onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))} placeholder="johndoe" />
                                </div>
                                <div className="wu-field">
                                    <label htmlFor="wu-password">Password</label>
                                    <input id="wu-password" type="password" value={formData.password} onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))} placeholder={showEditModal ? 'Leave blank to keep' : 'Required'} />
                                </div>
                            </div>
                            <div className="wu-form-row" style={{ marginTop: 12 }}>
                                <div className="wu-field">
                                    <label htmlFor="wu-fullname">Full name</label>
                                    <input id="wu-fullname" type="text" value={formData.fullName} onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))} />
                                </div>
                                <div className="wu-field">
                                    <label htmlFor="wu-email">Email</label>
                                    <input id="wu-email" type="email" value={formData.email} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} />
                                </div>
                            </div>
                            <div className="wu-form-row" style={{ marginTop: 12 }}>
                                <div className="wu-field">
                                    <label htmlFor="wu-org">Organization</label>
                                    <input id="wu-org" type="text" value={formData.org_name} onChange={(e) => setFormData((prev) => ({ ...prev, org_name: e.target.value }))} />
                                </div>
                                <div className="wu-field">
                                    <label htmlFor="wu-phone">Phone</label>
                                    <input id="wu-phone" type="text" value={formData.phone} onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))} />
                                </div>
                            </div>
                            <div className="wu-form-row" style={{ marginTop: 12 }}>
                                <div className="wu-field">
                                    <label htmlFor="wu-plan">Plan</label>
                                    <select id="wu-plan" value={formData.subscriptionStatus} onChange={(e) => setFormData((prev) => ({ ...prev, subscriptionStatus: e.target.value }))}>
                                        <option value="free">Free</option>
                                        <option value="trial">Trial</option>
                                        <option value="active">Active</option>
                                        <option value="expired">Expired</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="wu-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <label className="wu-toggle-row">
                                        <input type="checkbox" checked={formData.emailVerified} onChange={() => setFormData((prev) => ({ ...prev, emailVerified: !prev.emailVerified }))} />
                                        Email verified
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="wu-modal-footer">
                            <button type="button" className="wu-btn" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>Cancel</button>
                            <button type="button" className="wu-btn wu-btn--primary" onClick={showAddModal ? handleCreateFinish : handleEditFinish} disabled={isProcessing}>
                                {isProcessing ? 'Saving…' : showAddModal ? 'Create user' : 'Save changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Details modal */}
            {showDetailsModal && (
                <div className="wu-overlay" onClick={() => setShowDetailsModal(null)}>
                    <div className="wu-modal wu-modal--lg" onClick={(e) => e.stopPropagation()}>
                        <div className="wu-modal-header">
                            <h3 className="wu-modal-title">{showDetailsModal.fullName || showDetailsModal.username}</h3>
                            <button type="button" className="wu-modal-close" onClick={() => setShowDetailsModal(null)} aria-label="Close">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="wu-modal-body">
                            <div className="wu-detail-grid">
                                <div className="wu-detail-section">
                                    <h4>Account</h4>
                                    <div className="wu-detail-row"><span>Username</span><span>@{showDetailsModal.username}</span></div>
                                    <div className="wu-detail-row"><span>Email</span><span>{showDetailsModal.email}</span></div>
                                    <div className="wu-detail-row"><span>Organization</span><span>{showDetailsModal.org_name || '—'}</span></div>
                                    <div className="wu-detail-row">
                                        <span>Verified</span>
                                        <span>{showDetailsModal.emailVerified ? <CheckCircle size={14} style={{ color: '#059669' }} /> : 'No'}</span>
                                    </div>
                                    <div className="wu-detail-row"><span>Plan</span><span className={getStatusClass(showDetailsModal.subscriptionStatus)}>{showDetailsModal.subscriptionStatus || 'free'}</span></div>
                                    <div className="wu-detail-row"><span>Joined</span><span>{formatDate(showDetailsModal.createdAt)}</span></div>
                                </div>
                                <div className="wu-detail-section">
                                    <h4>License</h4>
                                    {showDetailsModal.linked_fingerprint ? (
                                        <>
                                            <div className="wu-detail-row">
                                                <span>Status</span>
                                                <span className={getStatusClass(showDetailsModal.linked_fingerprint.status)}>{showDetailsModal.linked_fingerprint.status}</span>
                                            </div>
                                            <div className="wu-detail-row">
                                                <span>Expires</span>
                                                <span>{formatDate(showDetailsModal.linked_fingerprint.expires_at || '')}</span>
                                            </div>
                                            {showDetailsModal.linked_fingerprint.limits && (
                                                <>
                                                    <div className="wu-detail-row">
                                                        <span>Cameras</span>
                                                        <span>{showDetailsModal.linked_fingerprint.limits.max_cameras ?? 0}</span>
                                                    </div>
                                                    <div className="wu-detail-row">
                                                        <span>Members</span>
                                                        <span>{showDetailsModal.linked_fingerprint.limits.max_members ?? 0}</span>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="wu-detail-empty">
                                            <p>No linked license</p>
                                            <button type="button" className="wu-btn wu-btn--primary" style={{ marginTop: 8 }} onClick={() => { handleSyncUser(showDetailsModal._id); setShowDetailsModal(null); }}>
                                                Sync license
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="wu-modal-footer">
                            <button type="button" className="wu-btn" onClick={() => setShowDetailsModal(null)}>Close</button>
                            <button type="button" className="wu-btn" onClick={() => { handleEdit(showDetailsModal); setShowDetailsModal(null); }}>
                                <Edit size={14} /> Edit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation */}
            {showDeleteConfirm && (
                <div className="wu-overlay" onClick={() => setShowDeleteConfirm(null)}>
                    <div className="wu-modal wu-modal--sm" onClick={(e) => e.stopPropagation()}>
                        <div className="wu-modal-body">
                            <div className="wu-confirm-icon"><Trash2 size={22} /></div>
                            <p className="wu-confirm-text">Delete this website user? This cannot be undone.</p>
                        </div>
                        <div className="wu-modal-footer" style={{ justifyContent: 'center' }}>
                            <button type="button" className="wu-btn" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
                            <button type="button" className="wu-btn wu-btn--danger" onClick={() => handleDelete(showDeleteConfirm)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {showBulkDeleteConfirm && (
                <div className="wu-overlay" onClick={() => { setShowBulkDeleteConfirm(false); setBulkDeleteFingerprints(false); }}>
                    <div className="wu-modal wu-modal--sm" onClick={(e) => e.stopPropagation()}>
                        <div className="wu-modal-body">
                            <div className="wu-confirm-icon"><AlertTriangle size={22} /></div>
                            <p className="wu-confirm-text">
                                Delete {selectedUserIds.size} selected user{selectedUserIds.size === 1 ? '' : 's'}?
                            </p>
                            <label className="wu-toggle-row" style={{ justifyContent: 'center', marginTop: 8 }}>
                                <input type="checkbox" checked={bulkDeleteFingerprints} onChange={(e) => setBulkDeleteFingerprints(e.target.checked)} />
                                Also delete linked license records
                            </label>
                        </div>
                        <div className="wu-modal-footer" style={{ justifyContent: 'center' }}>
                            <button type="button" className="wu-btn" onClick={() => { setShowBulkDeleteConfirm(false); setBulkDeleteFingerprints(false); }} disabled={isProcessing}>Cancel</button>
                            <button type="button" className="wu-btn wu-btn--danger" onClick={() => handleBulkDelete(bulkDeleteFingerprints)} disabled={isProcessing}>
                                {isProcessing ? 'Deleting…' : 'Delete selected'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InukaUsersManager;
